/* eslint-disable */
// @ts-ignore
import React, { useEffect, useRef, useState } from "react";
import { useForms } from "../context/store";
import { StepHelper, FormContainer, FormSubmitter, IdentityInfo, isInArray, isNull, 
    isNullOrEmpty, FormSubmitModel, FormSubmitResult, SubmitButton, FormCache, 
    FormConstants, ProblemDetail, StepDependCondition, getConfirmationData, FormStorage } from "@episerver/forms-sdk";
import { RenderElementInStep } from "./RenderElementInStep";
import { DispatchFunctions } from "../context/dispatchFunctions";
import { FormStepNavigation } from "./FormStepNavigation";
import useAuthorizeToken from "4-sections/DonationBlock/AuthorizePayment/useAuthorizeToken";
import { useDonationStore } from "4-sections/DonationBlock/libs/useDonationStore";
import LoadingIndicator from "3-components/LoadingIndicator/LoadingIndicator";
import { DONATION_FORM_NAME, resetFocusSectionDonation } from "4-sections/DonationBlock/libs/common";
import { PAYMENT_TYPE, CREDIT_CARD_PROVIDER } from "4-sections/DonationBlock/libs/common";
import useIPGPayment from "4-sections/DonationBlock/IPGPayment/useIPGPayment";
import useBamboraPayment from "4-sections/DonationBlock/BamboraPayment/useBamboraPayment";
import useDebitPayment from "4-sections/DonationBlock/DebitPayment/useDebitPayment";
import { pushDataLayerOnFormComplete, pushDataLayerOnFormError, pushDataLayerOnFormStepComplete, sha256Encription } from "3-components/Tracker/Tracker";
import getFieldKey from "../helper/getFieldKey";
import { paymentHandler } from "3-components/Donations/Payments/paymentHandler";
import { useDonationBlockProps } from "4-sections/DonationBlock/DonationBlockProvider";
import { usePayfurl } from "4-sections/DonationBlock/PayfurlPayment/usePayfurlPayment";
import validateRecaptchaThreshold from "../helper/validateRecaptchaThreshold";

interface FormBodyProps {
    identityInfo?: IdentityInfo;
    baseUrl: string;
    history?: any;
    currentPageUrl?: string;
}

export const FormBodyPersonalDetail = (props: FormBodyProps) => {
    const { donationData, setDonationData } = useDonationStore();
    const [ isFormLoading, setIsFormLoading ] = useState(false);
    
    // payment handling
    const { requestAuthorizeToken } = useAuthorizeToken();
    const { getIframeURL: getIPGPaymentURL } = useIPGPayment();
    const { getIframeURL: getBamboraPaymentURL } = useBamboraPayment();
    const { initDebitPayment } = useDebitPayment();
    const { PayfurlPaymentHandler } = usePayfurl();

    const { baseURL, reCaptchaValidationConfig } = useDonationBlockProps();

    //recaptcha validation will always triggered below this transaction amount
    const recaptchaThreshold = reCaptchaValidationConfig?.threshold ?? 100;

    const formContext = useForms();
    const form = formContext?.formContainer ?? {} as FormContainer;
    const inactiveElements = formContext?.dependencyInactiveElements ?? [];
    const formSubmitter = new FormSubmitter(form, props.baseUrl);
    const dispatchFunctions = new DispatchFunctions();
    const stepDependCondition = new StepDependCondition(form, inactiveElements);
    const stepHelper = new StepHelper(form);
    const currentPageUrl = props.currentPageUrl ?? window.location.href;
    const formStorage = new FormStorage(form);

    const formName = DONATION_FORM_NAME;
    const formTitleId = `${form.key}_label`;
    const statusMessage = useRef<string>("");
    const statusDisplay = useRef<string>("hide");
    
    const showForm = useRef<boolean>(true);

    const formCache = new FormCache();
    const localFormCache = new FormCache(window.localStorage);
    const currentStepIndex = formContext?.currentStepIndex ?? 0;

    //TODO: these variables should be get from api or sdk
    const validateFail = useRef<boolean>(false),
        isFormFinalized = useRef<boolean>(false),
        isProgressiveSubmit = useRef<boolean>(false),
        isSuccess = useRef<boolean>(false),
        submissionWarning = useRef<boolean>(false),
        message = useRef<string>(""),
        submissionStorageKey = FormConstants.FormSubmissionId + form.key,
        isStepValidToDisplay = stepDependCondition.isStepValidToDisplay(currentStepIndex, currentPageUrl),
        isMalFormSteps = stepHelper.isMalFormSteps();

    if ((isFormFinalized.current || isProgressiveSubmit.current) && isSuccess.current) {
        statusDisplay.current = "Form__Success__Message";
        statusMessage.current = form.properties.submitSuccessMessage ?? message.current;
    }
    else if ((submissionWarning.current || !isSuccess.current)
        && !isNullOrEmpty(message.current)) {
        statusDisplay.current = "Form__Warning__Message";
        statusMessage.current = message.current;
    }
    else {
        statusDisplay.current = "hide";
        statusMessage.current = "";
    }

    const validationCssClass = validateFail.current ? "ValidationFail" : "ValidationSuccess";

    const showError = (error: string) => {
        submissionWarning.current = !isNullOrEmpty(error);
        message.current = error;
    }

    const handleConfirm = () => {
        const form = formContext?.formContainer ?? {} as FormContainer;
        const confirmationMessage = form.properties.confirmationMessage ?? "";
        let confimStatus = true;

        if (form.properties.showSummarizedData) {
            const data = formContext?.formSubmissions ?? []
            const messageData = getConfirmationData(data, form, currentStepIndex, inactiveElements)
            const showConfirmationMessage = !(isNullOrEmpty(confirmationMessage) && isNullOrEmpty(messageData))
            if (showConfirmationMessage) {
                confimStatus = confirm(confirmationMessage + "\n\n" + messageData);
            }
        } 

        return confimStatus
    }

    const handleSubmit = (e: any) => {
        e.preventDefault();

        if (!form.properties.allowAnonymousSubmission && isNullOrEmpty(formContext?.identityInfo?.accessToken)) {
            return;
        }

        //Find submit button, if found then check property 'finalizeForm' of submit button. Otherwise, button Next/Previous was clicked.
        let buttonId = e.nativeEvent.submitter?.id;
        let submitButton = form.formElements.find(fe => fe.key === buttonId) as SubmitButton;
        if (!isNull(submitButton)) {
            //when submitting by SubmitButton, isProgressiveSubmit default is true
            isProgressiveSubmit.current = true;
        }

        let isLastStep = currentStepIndex === form.steps.length - 1;

         //get inactives element
         let inactives = (formContext?.elementDependencies ?? [])
         .filter(dependency => !dependency.isSatisfied)
         .map(dependency => dependency.elementKey);

        //filter submissions by active elements and current step
        let formSubmissions = (formContext?.formSubmissions ?? [])
            .filter(fs => !isInArray(fs.elementKey, inactives) && stepHelper.isInCurrentStep(fs.elementKey, currentStepIndex));

        //validate all submission data before submit
        let formValidationResults = formSubmitter.doValidate(formSubmissions);
        dispatchFunctions.updateAllValidation(formValidationResults);

        //set focus on the 1st invalid element of current step
        let invalid = stepHelper.getFirstInvalidElement(formValidationResults, currentStepIndex);
        if (!isNullOrEmpty(invalid)) {
            dispatchFunctions.updateFocusOn(invalid);
            return;
        }

        // Confirm if user want to submit the form
        if (!isNull(submitButton) || isLastStep) {
            if (!handleConfirm()) {
                isProgressiveSubmit.current = false
                return
            }
        }

        const newFormSubmissions = validateRecaptchaThreshold(formContext, formSubmissions, recaptchaThreshold);

        let model: FormSubmitModel = {
            formKey: form.key,
            locale: form.locale,
            isFinalized: submitButton?.properties?.finalizeForm || isLastStep,
            partialSubmissionKey: localFormCache.get(submissionStorageKey) ?? formContext?.submissionKey ?? "",
            hostedPageUrl: currentPageUrl,
            submissionData: newFormSubmissions,
            accessToken: formContext?.identityInfo?.accessToken,
            currentStepIndex: currentStepIndex
        }

        //submit data to API
        dispatchFunctions.updateIsSubmitting(true);
        setIsFormLoading(true);
        resetFocusSectionDonation();

        formSubmitter.doSubmit(model).then((response: FormSubmitResult) => {
            //go here, response.success always is true
            isSuccess.current = response.success;
            isFormFinalized.current = (submitButton?.properties?.finalizeForm || isLastStep) && response.success;
            dispatchFunctions.updateSubmissionKey(response.submissionKey);
            localFormCache.set(submissionStorageKey, response.submissionKey);

            if (isProgressiveSubmit.current) {
                message.current = response.messages.map(m => m.message).join("<br>");
                // showForm.current = false
            }

            if (isFormFinalized.current) {
                formCache.remove(FormConstants.FormCurrentStep + form.key);
                localFormCache.remove(submissionStorageKey);
                formStorage.saveFormDataToStorage(model.submissionData);
            }

            //redirect after submit
            if (submitButton) {
                let redirectToPage = submitButton?.properties?.redirectToPage ?? form.properties?.redirectToPage;
                if (!isNullOrEmpty(redirectToPage)) {
                    let url = new URL(redirectToPage, "http://temp");
                    window.location.href = url.href;
                }
            }
            
        }).catch((e: ProblemDetail) => {
            switch (e.status) {
                case 401:
                    //clear access token to ask login again
                    dispatchFunctions.updateIdentity({} as IdentityInfo);
                    formCache.remove(FormConstants.FormAccessToken);
                    break;
                case 400:
                    if (e.errors) {
                        //validate fail
                        validateFail.current = false;
                        let formValidationResults = formContext?.formValidationResults?.map(fr => isNull(e.errors[fr.elementKey]) ? fr : {
                            ...fr,
                            result: { valid: false, message: e.errors[fr.elementKey].join("<br/>") }
                        }) ?? [];

                        dispatchFunctions.updateAllValidation(formValidationResults);

                        //set focus on the 1st invalid element of current step
                        dispatchFunctions.updateFocusOn(stepHelper.getFirstInvalidElement(formValidationResults, currentStepIndex));
                    }
                    break;
            }

            const errorDetail = e.detail ?? 'error 403:';

            showError(errorDetail);
            setIsFormLoading(false);
            pushDataLayerOnFormError({
                formName,
                formType: 'donation',
                errorMessage: e.detail
            });
        }).finally(() => {
            dispatchFunctions.updateIsSubmitting(false);
        });
    }

    useEffect(() => {
        dispatchFunctions.updateIdentity(props.identityInfo);
        if (isNullOrEmpty(props.identityInfo?.accessToken) && !form.properties.allowAnonymousSubmission) {
            showError(form.localizations["allowAnonymousSubmissionErrorMessage"]);
        }
        else {
            showError("");
        }
    }, [props.identityInfo?.accessToken]);

    //reset when change page
    useEffect(() => {
        isSuccess.current = false;
        if (form.properties.focusOnForm || currentStepIndex > 0){            

            dispatchFunctions.updateFocusOn(stepHelper.getFirstInputElement(currentStepIndex, inactiveElements));

        }
    }, [currentStepIndex]);

    //Run in-case change page by url. The currentStepIndex that get from cache is incorrect.
    useEffect(() => {
        if (!isStepValidToDisplay) {
            dispatchFunctions.updateCurrentStepIndex(stepHelper.getCurrentStepIndex(currentPageUrl));
        }
    }, []);

    useEffect(() => {
        // if form already submitted
        if(isFormFinalized.current && formContext?.submissionKey) {

            // find email field value
            const emailFieldKey = getFieldKey(formContext, 'email');
            const emailFieldValue = formContext.formSubmissions.find(data => data.elementKey === emailFieldKey)?.value;
            const userId = emailFieldValue ? sha256Encription(emailFieldValue) : '';

            // GA4 dayaLayer => form complete
            pushDataLayerOnFormComplete({
                formName,
                formType: 'donation',
                userId,
            });

            // GA4 dataLayer => step complete
            pushDataLayerOnFormStepComplete({
                formName,
                formType: 'donation',
                stepName: 'personal detail',
            });

            // submission key will be used as donation id
            const submissionKey = formContext.submissionKey;

            setDonationData({ submissionKey }); // save the key

            // request payment url for IPG gateway
            if( donationData.payment === CREDIT_CARD_PROVIDER.IPG ) {
                getIPGPaymentURL(submissionKey);
            }

            // request payment token for Authorize.Net gateway
            if (donationData.payment === CREDIT_CARD_PROVIDER.AuthorizeNet) {
                requestAuthorizeToken(submissionKey);
            }

            // request payment url for Bambora gateway
            if (donationData.payment === CREDIT_CARD_PROVIDER.Bambora) {
                getBamboraPaymentURL(submissionKey);
            }

            // redirect to thank you page for paypal
            if( donationData.payment === PAYMENT_TYPE.PAYPAL ) {
                paymentHandler.Paypal(baseURL as string, submissionKey);
            }

            if( donationData.payment === CREDIT_CARD_PROVIDER.payFURL ) {
                PayfurlPaymentHandler(submissionKey);
            }

            if( donationData.payment === PAYMENT_TYPE.DIRECT_DEBIT ) {
                initDebitPayment(submissionKey)
            }
        }
    }, [isFormFinalized, formContext])

    isMalFormSteps && showError(form.localizations["malformstepconfigruationErrorMessage"]);

    return (
        <div className="custom-opti-form-wrapper">
            <div className={`custom-opti-loading-wrapper ${!isFormLoading && `is-hidden`}`}>
                <LoadingIndicator />
            </div>
            <form method="post"
                noValidate={true}
                aria-labelledby={formTitleId}
                encType="multipart/form-data"
                className={`EPiServerForms ${validationCssClass}`}
                id={form.key}
                onSubmit={handleSubmit}
            >
                {/* {form.properties.title &&
                    <h2 className="Form__Title" id={formTitleId}>
                        {form.properties.title}
                    </h2>
                } */}
                {form.properties.description &&
                    <aside className="Form__Description">
                        {form.properties.description}
                    </aside>
                }
                {/* area for showing Form's status or validation */}
                <div className="Form__Status">
                    <div role="status" className={`Form__Status__Message ${statusDisplay.current}`}>
                        <div dangerouslySetInnerHTML={{
                            __html: statusMessage.current
                        }}
                        ></div>
                    </div>
                </div>

                <div className="Form__MainBody" style={{display: showForm.current ? 'flow' : 'none' }}>
                    {/* render element */}
                    {form.steps.map((e, i) => {
                        let stepDisplaying = (currentStepIndex === i && !isFormFinalized.current && isStepValidToDisplay && !isMalFormSteps) ? "" : "hide";
                        return (
                            <section key={e.formStep.key} id={e.formStep.key} className={`Form__Element__Step ${stepDisplaying}`}>
                                <RenderElementInStep elements={e.elements} stepIndex={i} />
                            </section>
                        );
                    })}

                    {/* render step navigation*/}
                    <FormStepNavigation
                        isFormFinalized={isFormFinalized.current}
                        history={props.history}
                        handleSubmit={handleSubmit}
                        isMalFormSteps={isMalFormSteps}
                        isStepValidToDisplay={isStepValidToDisplay}
                        isSuccess={isSuccess.current}
                    />
                </div>
            </form>
        </div>
    )
}